import type {
  RegisterRequest,
  LoginRequest,
  AuthResponse,
  MeResponse,
  Setup2FAResponse,
  Verify2FARequest,
  Login2FARequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  ResendVerificationRequest,
  VerifyEmailResponse,
} from '@codedrill/shared';
import { apiFetch } from './client';

// ── Existing ──────────────────────────────────────────────────────────────────

export async function apiRegister(
  body: RegisterRequest,
): Promise<{ message: string }> {
  // Registration no longer returns tokens — user must verify email first
  return apiFetch('/auth/register', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function apiLogin(body: LoginRequest): Promise<AuthResponse> {
  return apiFetch<AuthResponse>('/auth/login', {
    method: 'POST',
    body: JSON.stringify(body),
    credentials: 'include',
  });
}

export async function apiLogout(): Promise<void> {
  await apiFetch<void>('/auth/logout', {
    method: 'POST',
    credentials: 'include',
  });
}

export async function apiRefreshToken(): Promise<{ accessToken: string }> {
  return apiFetch<{ accessToken: string }>('/auth/refresh', {
    method: 'POST',
    credentials: 'include',
  });
}

export async function apiGetMe(): Promise<MeResponse> {
  return apiFetch<MeResponse>('/auth/me');
}

// ── 2FA ───────────────────────────────────────────────────────────────────────

/** Initiate 2FA setup — requires a valid access token (user must be logged in). */
export async function api2FASetup(): Promise<Setup2FAResponse> {
  return apiFetch<Setup2FAResponse>('/auth/2fa/setup', { method: 'POST' });
}

/** Confirm the first TOTP code — enables 2FA on the account. */
export async function api2FAVerify(body: Verify2FARequest): Promise<{ message: string }> {
  return apiFetch<{ message: string }>('/auth/2fa/verify', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Complete login when 2FA is required.
 * Sends the short-lived pendingToken + 6-digit TOTP code.
 * Returns the same full AuthResponse as a normal login.
 */
export async function api2FALogin(body: Login2FARequest): Promise<AuthResponse> {
  return apiFetch<AuthResponse>('/auth/2fa/login', {
    method: 'POST',
    body: JSON.stringify(body),
    credentials: 'include',
  });
}

// ── Email verification ─────────────────────────────────────────────────────────

export async function apiVerifyEmail(token: string): Promise<VerifyEmailResponse> {
  return apiFetch<VerifyEmailResponse>(`/auth/verify-email?token=${encodeURIComponent(token)}`);
}

export async function apiResendVerification(
  body: ResendVerificationRequest,
): Promise<{ message: string }> {
  return apiFetch<{ message: string }>('/auth/resend-verification', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ── Password reset ─────────────────────────────────────────────────────────────

export async function apiForgotPassword(
  body: ForgotPasswordRequest,
): Promise<{ message: string }> {
  return apiFetch<{ message: string }>('/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function apiResetPassword(
  body: ResetPasswordRequest,
): Promise<{ message: string }> {
  return apiFetch<{ message: string }>('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ── Phone number ───────────────────────────────────────────────────────────────

export async function apiAddPhone(
  body: { phoneNumber: string },
): Promise<{ user: import('@codedrill/shared').AuthUser }> {
  return apiFetch('/auth/phone', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}
